import { useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import * as THREE from 'three'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import { prng, useIndeling } from './indeling'
import { palletY } from './trucks/werkcyclus'
import { useMaterialen, useObjecten, useOppervlak } from '../scene/store'
import { useOppervlakken } from './useOppervlakken'
import { variatieKleuren, vulInstanties } from './instanties'
import { verzendlabel } from './etiketten'
import { folie as folieTextuur } from './texturen'

/**
 * Een echte EUR-pallet: negen klossen, drie onderplanken, vijf bovenplanken,
 * samengevoegd tot één geometrie zodat hij nog steeds in één draw call instanced
 * kan worden. De pallet is het meest herhaalde object in de scène — één doos in
 * plaats van dit profiel is de duidelijkste "het zijn blokken"-tell.
 */
export function maakPalletGeometrie(): THREE.BufferGeometry {
  const delen: THREE.BufferGeometry[] = []
  const B = 1.2, D = 0.8

  const plank = (w: number, h: number, d: number, x: number, y: number, z: number) => {
    const g = new THREE.BoxGeometry(w, h, d)
    g.translate(x, y, z)
    delen.push(g)
  }

  // Onderplanken (3, in de breedte)
  for (const z of [-D / 2 + 0.05, 0, D / 2 - 0.05]) plank(B, 0.022, 0.1, 0, 0.011, z)
  // Klossen (9)
  for (const x of [-B / 2 + 0.05, 0, B / 2 - 0.05])
    for (const z of [-D / 2 + 0.05, 0, D / 2 - 0.05]) plank(0.1, 0.078, 0.1, x, 0.061, z)
  // Dwarsdragers (3, in de diepte)
  for (const x of [-B / 2 + 0.05, 0, B / 2 - 0.05]) plank(0.1, 0.022, D, x, 0.111, 0)
  // Bovenplanken (5)
  for (let i = 0; i < 5; i++) {
    const z = -D / 2 + 0.05 + (i * (D - 0.1)) / 4
    plank(B, 0.022, i === 0 || i === 4 ? 0.1 : 0.08, 0, 0.133, z)
  }

  const g = mergeGeometries(delen, false)!
  delen.forEach((x) => x.dispose())
  return g
}

/**
 * Zes ladingtypen in plaats van één grijze doos.
 *
 * `vrij`: vakken waar de K- en R-MATIC hun eigen pallets halen en brengen
 * (`werkgangen.ts`). Die blijven hier leeg — maar de toevalsgenerator loopt
 * er gewoon doorheen, anders schoof de hele stelling een plek op zodra het
 * aantal trucks verandert.
 */
type Vak = { z: number; bay: number; niveau: number }
const GEEN: Vak[] = []

export function Lading({ vrij = GEEN }: { vrij?: Vak[] }) {
  const ind = useIndeling()
  const mat = useMaterialen()
  const obj = useObjecten()
  const opp = useOppervlak()
  const set = useOppervlakken()

  const pallets = useRef<THREE.InstancedMesh>(null)
  const kartons = useRef<THREE.InstancedMesh>(null)
  const kisten = useRef<THREE.InstancedMesh>(null)
  const folie = useRef<THREE.InstancedMesh>(null)
  const bigbags = useRef<THREE.InstancedMesh>(null)
  const vaten = useRef<THREE.InstancedMesh>(null)
  const etiketten = useRef<THREE.InstancedMesh>(null)
  const banden = useRef<THREE.InstancedMesh>(null)

  const palletGeo = useMemo(() => maakPalletGeometrie(), [])
  const label = useMemo(() => verzendlabel(ind.cfg.seed + 3), [ind.cfg.seed])
  const folieMap = useMemo(() => folieTextuur(ind.cfg.seed + 8), [ind.cfg.seed])
  useEffect(() => () => { label.dispose(); folieMap.dispose() }, [label, folieMap])

  const opbouw = useMemo(() => {
    const { cfg, bayX, wanden } = ind
    const rnd = prng(cfg.seed)
    const d = new THREE.Object3D()

    const palletM: THREE.Matrix4[] = []
    const kartonM: THREE.Matrix4[] = []
    const kistM: THREE.Matrix4[] = []
    const folieM: THREE.Matrix4[] = []
    const bagM: THREE.Matrix4[] = []
    const vatM: THREE.Matrix4[] = []
    const etiketM: THREE.Matrix4[] = []
    const bandM: THREE.Matrix4[] = []

    let weg = false
    const zet = (arr: THREE.Matrix4[]) => {
      if (weg) return
      d.updateMatrix()
      arr.push(d.matrix.clone())
    }

    for (const wand of wanden) {
      for (let b = 0; b < cfg.bays; b++) {
        if (b === ind.dwarsgang) continue
        for (let n = 0; n < cfg.niveaus; n++) {
          if (rnd() > cfg.vulgraad) continue
          weg = vrij.some((v) => v.bay === b && v.niveau === n && Math.abs(v.z - wand.z) < 0.01)

          const y = palletY(n)
          const x = bayX(b) + (rnd() - 0.5) * 0.14
          const draai = (rnd() - 0.5) * 0.06

          d.position.set(x, y, wand.z)
          d.rotation.set(0, draai, 0)
          d.scale.set(1, 1, 1)
          zet(palletM)

          const top = y + 0.155
          const type = rnd()

          if (type < 0.4) {
            // Gestapelde kartons: twee tot drie lagen
            const lagen = 2 + Math.floor(rnd() * 2)
            let h = top
            let voorkant = 0.3
            let laagZ = wand.z
            let laagDraai = draai
            for (let l = 0; l < lagen; l++) {
              const lh = 0.3 + rnd() * 0.22
              laagZ = wand.z + (rnd() - 0.5) * 0.05
              laagDraai = draai + (rnd() - 0.5) * 0.08
              const diepte = 0.74 + rnd() * 0.1
              d.position.set(x + (rnd() - 0.5) * 0.06, h + lh / 2, laagZ)
              d.rotation.set(0, laagDraai, 0)
              d.scale.set(0.9 + rnd() * 0.16, lh, diepte)
              zet(kartonM)
              voorkant = (0.78 * diepte) / 2
              h += lh
            }
            // Verzendlabel op de bovenste laag, aan de gangpadkant: daar kijkt
            // de camera, en daar plakt een orderpicker hem ook.
            const kant = wand.kant === -1 ? 1 : -1
            d.position.set(x + 0.22 + (rnd() - 0.5) * 0.12, h - 0.16, laagZ + kant * (voorkant + 0.004))
            d.rotation.set(0, (kant === 1 ? 0 : Math.PI) + laagDraai, 0); d.scale.set(1, 1, 1)
            zet(etiketM)
          } else if (type < 0.58) {
            // Eén grote kist
            const h = 0.85 + rnd() * 0.4
            d.position.set(x, top + h / 2, wand.z)
            d.rotation.set(0, draai, 0)
            d.scale.set(1, h / 1.1, 1)
            zet(kistM)
          } else if (type < 0.78) {
            // Gewikkelde stapel: kartons met een folieschil eromheen
            const h = 0.9 + rnd() * 0.35
            d.position.set(x, top + h / 2, wand.z)
            d.rotation.set(0, draai, 0)
            d.scale.set(1, h / 1.0, 1)
            zet(kartonM)
            d.scale.set(1.04, h / 1.0 * 1.02, 1.05)
            zet(folieM)
            // Spanbanden
            for (const f of [0.35, 0.7]) {
              d.position.set(x, top + h * f, wand.z)
              d.scale.set(1.06, 1, 1.07)
              zet(bandM)
            }
          } else if (type < 0.9) {
            // Big bag
            const h = 0.95 + rnd() * 0.25
            d.position.set(x, top + h / 2, wand.z)
            d.rotation.set(0, draai, 0)
            d.scale.set(1, h / 1.0, 1)
            zet(bagM)
          } else {
            // Vaten: vier op een pallet
            for (const [ox, oz] of [[-0.26, -0.18], [0.26, -0.18], [-0.26, 0.18], [0.26, 0.18]]) {
              d.position.set(x + ox, top + 0.42, wand.z + oz)
              d.rotation.set(0, rnd() * 3, 0)
              d.scale.set(1, 1, 1)
              zet(vatM)
            }
          }
        }
      }
    }
    return { palletM, kartonM, kistM, folieM, bagM, vatM, etiketM, bandM }
  }, [ind, vrij])

  /**
   * Kleurafwijking per stuk lading.
   *
   * Karton krijgt de ruimste marge van de hele scène, en met reden: gebleekt
   * wit karton en gerecycled bruin karton staan in een echt magazijn naast
   * elkaar op dezelfde pallet. Dit is precies het effect dat hier al bedoeld
   * was maar nooit in beeld kwam — zie de toelichting in `instanties.ts`.
   */
  const kleuren = useMemo(() => {
    const v = opp.variatie
    const s = ind.cfg.seed
    return {
      pallet: variatieKleuren(opbouw.palletM.length, v * 1.4, s + 11),
      karton: variatieKleuren(opbouw.kartonM.length, v * 1.6, s + 12),
      kist: variatieKleuren(opbouw.kistM.length, v * 1.3, s + 13),
      bag: variatieKleuren(opbouw.bagM.length, v * 1.1, s + 14),
      vat: variatieKleuren(opbouw.vatM.length, v * 1.2, s + 15),
    }
  }, [opbouw, opp.variatie, ind.cfg.seed])

  useLayoutEffect(() => {
    vulInstanties(pallets.current, opbouw.palletM, kleuren.pallet)
    vulInstanties(kartons.current, opbouw.kartonM, kleuren.karton)
    vulInstanties(kisten.current, opbouw.kistM, kleuren.kist)
    vulInstanties(folie.current, opbouw.folieM)
    vulInstanties(bigbags.current, opbouw.bagM, kleuren.bag)
    vulInstanties(vaten.current, opbouw.vatM, kleuren.vat)
    vulInstanties(etiketten.current, opbouw.etiketM)
    vulInstanties(banden.current, opbouw.bandM)
  }, [opbouw, kleuren])

  if (!obj.lading) return null

  const tex = (soort: 'hout' | 'karton' | 'doos', sterkte = 1) =>
    set
      ? {
          map: set[soort].map,
          roughnessMap: set[soort].roughnessMap,
          normalMap: set[soort].normalMap,
          normalScale: new THREE.Vector2(opp.relief * sterkte, opp.relief * sterkte),
        }
      : {}

  return (
    <group>
      <instancedMesh ref={pallets} args={[palletGeo, null!, opbouw.palletM.length]} castShadow receiveShadow>
        <meshStandardMaterial color={mat.pallet} roughness={0.92} {...tex('hout')} />
      </instancedMesh>

      {/* `mat.karton` hoort hier: de Karton-kleur in de Studio stond tot nu toe
          alleen op de lading van de T-Matic, niet op de 900 dozen in de hal. */}
      <instancedMesh ref={kartons} args={[null!, null!, opbouw.kartonM.length]} castShadow receiveShadow>
        <boxGeometry args={[1.08, 1, 0.78]} />
        <meshStandardMaterial color={mat.karton} roughness={0.88} {...tex('doos')} />
      </instancedMesh>

      <instancedMesh ref={kisten} args={[null!, null!, opbouw.kistM.length]} castShadow receiveShadow>
        <boxGeometry args={[1.1, 1.1, 0.8]} />
        <meshStandardMaterial color="#6d5a3f" roughness={0.85} {...tex('hout', 0.8)} />
      </instancedMesh>

      {/* Krimpfolie: licht doorschijnend en iets blauwig, zoals echte wikkelfolie. */}
      <instancedMesh ref={folie} args={[null!, null!, opbouw.folieM.length]}>
        <boxGeometry args={[1.08, 1, 0.78]} />
        {/* Glad genoeg om de armaturen te spiegelen: dat, en niet de kleur, is
            waaraan je folie herkent. */}
        <meshStandardMaterial
          color="#d5e3ea" transparent alphaMap={folieMap} opacity={0.9} roughness={0.12}
          metalness={0} depthWrite={false} envMapIntensity={1.6}
        />
      </instancedMesh>

      <instancedMesh ref={bigbags} args={[null!, null!, opbouw.bagM.length]} castShadow>
        {/* Licht taps toelopend: een big bag hangt breder van boven. */}
        <cylinderGeometry args={[0.52, 0.46, 1.0, 4, 1]} />
        <meshStandardMaterial color="#d8d2c2" roughness={0.95} {...tex('karton', 0.7)} />
      </instancedMesh>

      <instancedMesh ref={vaten} args={[null!, null!, opbouw.vatM.length]} castShadow>
        <cylinderGeometry args={[0.24, 0.24, 0.84, 14]} />
        <meshStandardMaterial color="#2f6d8a" roughness={0.45} metalness={0.35} />
      </instancedMesh>

      <instancedMesh ref={etiketten} args={[null!, null!, opbouw.etiketM.length]}>
        <planeGeometry args={[0.2, 0.15]} />
        <meshStandardMaterial map={label} roughness={0.7} polygonOffset polygonOffsetFactor={-1} />
      </instancedMesh>

      <instancedMesh ref={banden} args={[null!, null!, opbouw.bandM.length]}>
        <boxGeometry args={[1.08, 0.025, 0.78]} />
        <meshStandardMaterial color="#25313d" roughness={0.6} />
      </instancedMesh>
    </group>
  )
}
